import { get, noop } from "lodash-es";
import { call, put, takeLatest } from "redux-saga/effects";
import {
  fetch_diy_categories,
  fetch_trending_funds,
  fetch_sub_category_fund_list,
  fetch_search_results,
} from "../../apis/diy";
import { mapViewTypeForSubcategories } from "../../utils/diy/functions";
import {
  fetchDiyCategories,
  setTrendingFunds,
  fetchTrendingFunds,
  setFundList,
  fetchFundList,
  setDiyCategories,
  getSearchFunds,
  setSearchFunds,
  fetchDiyCategoriesAndTrendingFunds,
  setFilteredFundList,
  setFundHouses,
  getDiyDataForFundListDeeplink,
  setDiyTypeData,
  setFundsCart,
  resetFilters,
} from "../reducers/diy";
import { setError } from "../reducers/error";
import { setLoader } from "../reducers/loader";

export default function* sagaFetchDiyData() {
  yield takeLatest(fetchDiyCategories.toString(), handleFetchDiyCategories);
  yield takeLatest(fetchTrendingFunds.toString(), handleFetchTrendingFunds);
  yield takeLatest(fetchFundList.toString(), handleFetchDiyFundList);
  yield takeLatest(
    fetchDiyCategoriesAndTrendingFunds.toString(),
    handleFetchDiyCategoriesAndTrendingFunds
  );
  yield takeLatest(getSearchFunds.toString(), handleSearchFunds);
  yield takeLatest(
    getDiyDataForFundListDeeplink.toString(),
    handleDiyDataForFundListDeepLink
  );
}

function* handleSearchFunds({ payload }) {
  const { Api, screen, token, searchTerm } = payload;
  try {
    yield put(setLoader({ screen, isPageLoading: true }));
    yield put(
      setError({
        screen,
        isFetchFailed: false,
        isUpdateFailed: false,
        message: "",
      })
    );
    const response = yield call(fetch_search_results, Api, token, searchTerm);
    const searchResults = get(response, "funds", []);
    yield put(setSearchFunds(searchResults));
  } catch (err) {
    console.log("error is", err);
    yield put(setError({ screen, isFetchFailed: true, message: err.message }));
  } finally {
    yield put(setLoader({ screen, isPageLoading: false }));
  }
}

function* handleFetchDiyCategories({ payload }) {
  const { Api, screen, token } = payload;
  try {
    yield put(setLoader({ screen, isPageLoading: true }));
    yield put(
      setError({
        screen,
        isFetchFailed: false,
        isUpdateFailed: false,
        message: "",
      })
    );
    const response = yield call(fetch_diy_categories, Api, token);
    let categories = get(response, "result.classification", []);
    categories = mapViewTypeForSubcategories(categories);
    const fundHouses = get(response, "result.fund_houses", []);
    yield put(setFundHouses(fundHouses));
    yield put(setDiyCategories(categories));
  } catch (err) {
    console.log("error is", err);
    yield put(setError({ screen, isFetchFailed: true, message: err.message }));
  } finally {
    yield put(setLoader({ screen, isPageLoading: false }));
  }
}

function* handleFetchTrendingFunds({ payload }) {
  const { Api, screen, token } = payload;
  try {
    yield put(setLoader({ screen, isTrendingFundsLoading: true }));
    yield put(
      setError({
        screen,
        isFetchFailed: false,
        isUpdateFailed: false,
        message: "",
      })
    );
    const response = yield call(fetch_trending_funds, Api, token);
    const trendingFunds = get(response, "trends", {});
    yield put(setTrendingFunds(trendingFunds));
  } catch (err) {
    console.log("error is", err);
    yield put(setError({ screen, isFetchFailed: true, message: err.message }));
  } finally {
    yield put(setLoader({ screen, isTrendingFundsLoading: false }));
  }
}

function* handleFetchDiyCategoriesAndTrendingFunds({ payload }) {
  const { Api, screen, token } = payload;
  try {
    yield put(setLoader({ screen, isPageLoading: true }));
    yield put(
      setError({
        screen,
        isFetchFailed: false,
        isUpdateFailed: false,
        message: "",
      })
    );
    const response = yield call(fetch_diy_categories, Api, token);
    let categories = get(response, "result.classification", []);
    categories = mapViewTypeForSubcategories(categories);
    const fundHouses = get(response, "result.fund_houses", []);
    yield put(setFundHouses(fundHouses));
    yield put(setDiyCategories(categories));
    const trendingFundsResponse = yield call(fetch_trending_funds, Api, token);
    const trendingFunds = get(trendingFundsResponse, "trends", {});
    yield put(setTrendingFunds(trendingFunds));
  } catch (err) {
    console.log("error is", err);
    yield put(setError({ screen, isFetchFailed: true, message: err.message }));
  } finally {
    yield put(setLoader({ screen, isPageLoading: false }));
  }
}

function* handleFetchDiyFundList({ payload }) {
  const {
    Api,
    category,
    subcategory,
    subcategoryOption,
    token,
    screen,
    sagaCallback = noop,
  } = payload;
  try {
    yield put(setLoader({ screen, isPageLoading: true }));
    yield put(
      setError({
        screen,
        isFetchFailed: false,
        isUpdateFailed: false,
        message: "",
      })
    );
    const response = yield call(fetch_sub_category_fund_list, {
      Api,
      category,
      subcategoryOption,
      token,
    });
    const fundList = get(response, "funds");
    yield put(setFundList({ fundList, category, subcategoryOption }));
    yield put(
      setFilteredFundList({ category, subcategory, subcategoryOption })
    );
    sagaCallback();
  } catch (error) {
    console.log(error);
    yield put(
      setError({ screen, isFetchFailed: true, message: error.message })
    );
  } finally {
    yield put(setLoader({ screen, isPageLoading: false }));
  }
}

// This is required, to fetch the required data to navigate to fundList directly using deeplink
function* handleDiyDataForFundListDeepLink({ payload }) {
  const {
    Api,
    screen,
    token,
    category,
    subcategory,
    subcategoryOption,
    sagaCallback = noop,
  } = payload;
  try {
    yield put(setLoader({ screen, isPageLoading: true }));
    yield put(
      setError({
        screen,
        isFetchFailed: false,
        isUpdateFailed: false,
        message: "",
      })
    );
    const response = yield call(fetch_diy_categories, Api, token);
    let categories = get(response, "result.classification", []);
    categories = mapViewTypeForSubcategories(categories);
    const fundHouses = get(response, "result.fund_houses", []);
    yield put(setFundHouses(fundHouses));
    yield put(setDiyCategories(categories));

    // Clear previous cart/filter data
    setFundsCart([]);
    resetFilters();

    // Set diy category, subcategory, subcategoryOption
    const diyData = {
      category: category,
      subcategory: subcategory,
      subcategoryOption: subcategoryOption,
    };
    yield put(setDiyTypeData(diyData));
    if (sagaCallback) {
      sagaCallback();
    }
  } catch (err) {
    console.log("error", err);
    yield put(setError({ screen, isFetchFailed: true, message: err.message }));
  } finally {
    yield put(setLoader({ screen, isPageLoading: false }));
  }
}
